import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Dashboard } from '../components/admin/Dashboard';
import { BillboardControlPanel } from '../components/admin/BillboardControlPanel';
import BillboardDeviceSimulator from '../components/admin/BillboardDeviceSimulator';
import { BillboardHealthCard } from '../components/admin/BillboardHealthCard';
import { AdminPaymentsByCampaign } from '../components/admin/AdminPaymentsByCampaign';
import { SystemStatusCard } from '../components/admin/SystemStatusCard';
import { BillboardLaunchPanel } from '../components/admin/BillboardLaunchPanel';
import EmergencyBroadcastPanel from '../components/admin/EmergencyBroadcastPanel';
import Billboard from './Billboard';
import { useAuth } from '../hooks/useAuth';
import { hasAdminPermission, isOwnerAdmin } from '../utils/adminPermissions';

const Admin: React.FC = () => {
  const { user } = useAuth();
  const [simulatedSpeedMph, setSimulatedSpeedMph] = useState<number>(35);
  const [showLaunchManager, setShowLaunchManager] = useState(false);
  const isFullAdmin = isOwnerAdmin(user);

  return (
    <main className="mx-auto max-w-6xl px-4 py-8 space-y-6">
      <header className="mb-4 flex items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-semibold text-slate-900 dark:text-slate-100">Admin Panel</h1>
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium ${
                isFullAdmin
                  ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100'
                  : 'bg-sky-50 text-sky-700 ring-1 ring-sky-100'
              }`}
            >
              {isFullAdmin ? 'Full admin' : 'Creator admin'}
            </span>
          </div>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Monitor performance, manage users, and tune your billboard network.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/share"
            className="inline-flex items-center rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-700 focus:ring-offset-2"
          >
            Create / share ad
          </Link>
          <Link
            to="/"
            className="inline-flex items-center rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-300 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Go home
          </Link>
        </div>
      </header>

      <section className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm">
          <Dashboard
            billboardPreview={
              hasAdminPermission(user, 'billboardTools') ? (
                <div className="relative h-[18rem] w-full overflow-hidden rounded-lg bg-black sm:h-[22rem] lg:h-[26rem]">
                  <Billboard
                    simulatedSpeedMph={simulatedSpeedMph}
                    onSimulatedSpeedChange={setSimulatedSpeedMph}
                    showDebugOverlay={false}
                    fullScreen={false}
                    mode="preview"
                  />
                </div>
              ) : null
            }
          />
        </div>
        <div className="space-y-4">
          <SystemStatusCard />
          {hasAdminPermission(user, 'billboardTools') && (
            <>
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm">
                <BillboardControlPanel
                  simulatedSpeedMph={simulatedSpeedMph}
                  onSimulatedSpeedChange={setSimulatedSpeedMph}
                />
              </div>
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm">
                <BillboardDeviceSimulator />
              </div>
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm">
                <BillboardHealthCard />
              </div>
            </>
          )}
        </div>
      </section>

      {/* Billboard Launch Manager */}
      {hasAdminPermission(user, 'billboardTools') && (
        <section>
          <button
            onClick={() => setShowLaunchManager((prev) => !prev)}
            className="flex w-full items-center justify-between rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-5 py-3 shadow-sm transition hover:bg-slate-50"
          >
            <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">Billboard Launch Manager</span>
            <span className="text-xs text-slate-400">{showLaunchManager ? 'Hide ▲' : 'Open ▼'}</span>
          </button>
          {showLaunchManager && (
            <div className="mt-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm">
              <BillboardLaunchPanel />
            </div>
          )}
        </section>
      )}

      {/* Emergency Broadcast */}
      {hasAdminPermission(user, 'billboardTools') && (
        <section className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm">
          <EmergencyBroadcastPanel />
        </section>
      )}

      <section className="space-y-4">
        {hasAdminPermission(user, 'payments') && (
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm">
            <AdminPaymentsByCampaign />
          </div>
        )}
      </section>
    </main>
  );
};

export default Admin;
